import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCompanyProfile, saveCompanyProfile } from "./CompanyProfileService";
import { FaCog, FaSave, FaArrowLeft } from "react-icons/fa";

const CompanyProfile = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    company_name: "",
    address: "",
    email: "",
    mobile: "",
    phone: "",
    website: "",
    tax_registration_number: "",
    social_links: {
      facebook: "",
      linkedin: "",
    },
  });

  const [files, setFiles] = useState({
    logo: null,
    stamp: null,
    signature: null,
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const data = await getCompanyProfile();
      if (data) {
        setForm({
          ...data,
          social_links: data.social_links || {},
        });
      }
    } catch (err) {
      console.log("No company profile yet");
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSocialChange = (e) => {
    setForm({
      ...form,
      social_links: {
        ...form.social_links,
        [e.target.name]: e.target.value,
      },
    });
  };

  const handleFileChange = (e) => {
    setFiles({ ...files, [e.target.name]: e.target.files[0] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData();

    // Only append fields expected by the backend Form
    const allowedFields = [
      "company_name", "address", "email", "mobile", 
      "phone", "website", "tax_registration_number", "social_links"
    ];

    allowedFields.forEach((key) => {
      if (key === "social_links") {
        formData.append(key, JSON.stringify(form[key]));
      } else {
        formData.append(key, form[key] || "");
      }
    });

    Object.keys(files).forEach((key) => {
      if (files[key]) {
        formData.append(key, files[key]);
      }
    });

    try {
      await saveCompanyProfile(formData);
      alert("Company profile saved successfully");
    } catch (error) {
      alert("Error saving company profile");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid #d0e8ff',
    background: '#f8fbff',
    color: '#333',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s',
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '6px',
    fontWeight: '500',
    color: '#333',
    fontSize: '14px',
  };

  const fieldGroup = {
    marginBottom: '18px',
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* Page Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '42px',
            height: '42px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #00aaff, #0077cc)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '18px',
          }}>
            <FaCog />
          </div>
          <div>
            <h4 style={{ margin: 0, color: '#1a1a2e', fontWeight: '600' }}>Company Profile</h4>
            <p style={{ margin: 0, fontSize: '13px', color: '#888' }}>Settings → Company Profile</p>
          </div>
        </div>
        <button
          onClick={() => navigate("/dashboard")}
          style={{
            background: '#f0f7ff',
            border: '1px solid #d0e8ff',
            color: '#0077cc',
            padding: '8px 18px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            transition: 'background 0.2s',
          }}
          onMouseEnter={(e) => e.target.style.background = '#e0efff'}
          onMouseLeave={(e) => e.target.style.background = '#f0f7ff'}
        >
          <FaArrowLeft /> Back to Dashboard
        </button>
      </div>

      {/* Settings Navigation Tabs */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        <button
          type="button"
          style={{
            padding: '8px 18px',
            borderRadius: '8px',
            background: '#0077cc',
            color: '#fff',
            border: 'none',
            fontWeight: '600',
            fontSize: '13px',
            cursor: 'default',
          }}
        >
          Company Profile
        </button>
        <button
          type="button"
          onClick={() => navigate("/dashboard/settings/permissions")}
          style={{
            padding: '8px 18px',
            borderRadius: '8px',
            background: '#ffffff',
            color: '#475569',
            border: '1px solid #cbd5e1',
            fontWeight: '600',
            fontSize: '13px',
            cursor: 'pointer',
          }}
        >
          🛡️ Module Permissions
        </button>
        <button
          type="button"
          onClick={() => navigate("/dashboard/templates")}
          style={{
            padding: '8px 18px',
            borderRadius: '8px',
            background: '#ffffff',
            color: '#475569',
            border: '1px solid #cbd5e1',
            fontWeight: '600',
            fontSize: '13px',
            cursor: 'pointer',
          }}
        >
          📄 Document Templates
        </button>
      </div>

      {/* Form Card */}
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        border: '1px solid #e0f0ff',
        padding: '28px',
        boxShadow: '0 2px 12px rgba(0, 119, 204, 0.06)',
      }}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
            {/* Company Name */}
            <div style={fieldGroup}>
              <label style={labelStyle}>Company Name *</label>
              <input
                name="company_name"
                value={form.company_name}
                onChange={handleChange}
                required
                style={inputStyle}
                onFocus={(e) => e.target.style.borderColor = '#00aaff'}
                onBlur={(e) => e.target.style.borderColor = '#d0e8ff'}
              />
            </div>

            {/* Email */}
            <div style={fieldGroup}>
              <label style={labelStyle}>Email</label>
              <input
                name="email"
                value={form.email || ""}
                onChange={handleChange}
                style={inputStyle}
                onFocus={(e) => e.target.style.borderColor = '#00aaff'}
                onBlur={(e) => e.target.style.borderColor = '#d0e8ff'}
              />
            </div>

            {/* Mobile */}
            <div style={fieldGroup}>
              <label style={labelStyle}>Mobile</label>
              <input
                name="mobile"
                value={form.mobile || ""}
                onChange={handleChange}
                style={inputStyle}
                onFocus={(e) => e.target.style.borderColor = '#00aaff'}
                onBlur={(e) => e.target.style.borderColor = '#d0e8ff'}
              />
            </div>

            {/* Phone */}
            <div style={fieldGroup}>
              <label style={labelStyle}>Phone</label>
              <input
                name="phone"
                value={form.phone || ""}
                onChange={handleChange}
                style={inputStyle}
                onFocus={(e) => e.target.style.borderColor = '#00aaff'}
                onBlur={(e) => e.target.style.borderColor = '#d0e8ff'}
              />
            </div>

            {/* Address - full width */}
            <div style={{ ...fieldGroup, gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Address</label>
              <textarea
                name="address"
                value={form.address || ""}
                onChange={handleChange}
                rows={3}
                style={{ ...inputStyle, resize: 'vertical' }}
                onFocus={(e) => e.target.style.borderColor = '#00aaff'}
                onBlur={(e) => e.target.style.borderColor = '#d0e8ff'}
              />
            </div>

            {/* Website */}
            <div style={fieldGroup}>
              <label style={labelStyle}>Website</label>
              <input
                name="website"
                value={form.website || ""}
                onChange={handleChange}
                style={inputStyle}
                onFocus={(e) => e.target.style.borderColor = '#00aaff'}
                onBlur={(e) => e.target.style.borderColor = '#d0e8ff'}
              />
            </div>

            {/* TRN */}
            <div style={fieldGroup}>
              <label style={labelStyle}>Tax Registration Number</label>
              <input
                name="tax_registration_number"
                value={form.tax_registration_number || ""}
                onChange={handleChange}
                style={inputStyle}
                onFocus={(e) => e.target.style.borderColor = '#00aaff'}
                onBlur={(e) => e.target.style.borderColor = '#d0e8ff'}
              />
            </div>

            {/* Facebook */}
            <div style={fieldGroup}>
              <label style={labelStyle}>Facebook</label>
              <input
                name="facebook"
                value={form.social_links.facebook || ""}
                onChange={handleSocialChange}
                style={inputStyle}
                onFocus={(e) => e.target.style.borderColor = '#00aaff'}
                onBlur={(e) => e.target.style.borderColor = '#d0e8ff'}
              />
            </div>

            {/* LinkedIn */}
            <div style={fieldGroup}>
              <label style={labelStyle}>LinkedIn</label>
              <input
                name="linkedin"
                value={form.social_links.linkedin || ""}
                onChange={handleSocialChange}
                style={inputStyle}
                onFocus={(e) => e.target.style.borderColor = '#00aaff'}
                onBlur={(e) => e.target.style.borderColor = '#d0e8ff'}
              />
            </div>
          </div>

          {/* File Uploads Section */}
          <div style={{
            marginTop: '8px',
            paddingTop: '20px',
            borderTop: '1px solid #e0f0ff',
          }}>
            <h6 style={{ color: '#0077cc', fontWeight: '600', marginBottom: '16px' }}>
              Upload Documents
            </h6>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
              <div>
                <label style={labelStyle}>Logo</label>
                <input
                  type="file"
                  name="logo"
                  onChange={handleFileChange}
                  style={{
                    ...inputStyle,
                    padding: '8px',
                    cursor: 'pointer',
                  }}
                />
              </div>
              <div>
                <label style={labelStyle}>Stamp</label>
                <input
                  type="file"
                  name="stamp"
                  onChange={handleFileChange}
                  style={{
                    ...inputStyle,
                    padding: '8px',
                    cursor: 'pointer',
                  }}
                />
              </div>
              <div>
                <label style={labelStyle}>Signature</label>
                <input
                  type="file"
                  name="signature"
                  onChange={handleFileChange}
                  style={{
                    ...inputStyle,
                    padding: '8px',
                    cursor: 'pointer',
                  }}
                />
              </div>
            </div>
          </div>

          {/* Save Button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              marginTop: '24px',
              background: 'linear-gradient(135deg, #00aaff, #0077cc)',
              border: 'none',
              color: '#fff',
              padding: '10px 28px',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: loading ? 0.7 : 1,
              transition: 'opacity 0.2s',
            }}
          >
            <FaSave /> {loading ? "Saving..." : "Save Company Profile"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CompanyProfile;
